/**
 * Whether there is a connection — and, just as importantly, what the studio does about it.
 *
 * Almost all of CreativeStudio is local: the canvas, the shapes, the text, drawing, cropping
 * (including the on-device matting in grabcut.js), the camera, the device library, saving to a
 * folder, printing, PDF. A handful of things are not: the AI tools, the signed-in cloud library,
 * and signing in itself. With no way to tell those apart, the app did the worst possible thing
 * with no connection — it let every button be pressed, then failed somewhere deep inside with a
 * message about a fetch, after a wait long enough to look like a hang.
 *
 * So the rule this module exists to support: anything that can work offline stays enabled, and
 * anything that genuinely cannot is disabled and says why, immediately, before it is pressed.
 *
 * The signal is navigator.onLine and only navigator.onLine — see the note on isOnline below for
 * what was tried instead and why it had to go.
 */

const listeners = new Set();

const notify = () => { for (const fn of listeners) { try { fn(isOnline()); } catch { /* a bad listener must not stop the others */ } } };

/**
 * The browser's own answer, and nothing else.
 *
 * ── Why there is no cleverness here any more ────────────────────────────────────────────
 * The first version of this file also let a failed request vote: a fetch that rejected the way
 * an unreachable network rejects marked the app offline until something got through. It took
 * one afternoon to find out why that is a trap.
 *
 * A fetch rejects identically whether the network is gone or the SERVER refused the preflight —
 * a CORS header that does not match is "Failed to fetch", the same words, with no way to tell
 * them apart from inside the page. This app has had exactly that bug before (the x-cs-device
 * header, added without adding it to the function's allowed list). So one badly configured
 * endpoint would flip the whole studio into offline mode.
 *
 * And then it could not get out. Being "offline" made callStudioAi refuse to send anything, so
 * no call could ever succeed to clear the flag, and the browser's `online` event never fires
 * because the browser never thought it was offline. Every AI button dead, the library switched
 * to the device, permanently, from one CORS error — a deadlock built out of a good intention.
 *
 * navigator.onLine is imperfect in one direction: it says true on a network that cannot reach
 * the internet. The cost of that is a button that can be pressed and then fails, which is what
 * the app has always done and is a great deal better than disabling itself by mistake.
 */
export function isOnline() {
  return !(typeof navigator !== 'undefined' && navigator.onLine === false);
}

/** Subscribe to changes. Returns an unsubscribe function. */
export function onNetChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', notify);
  window.addEventListener('offline', notify);
}
